import { useEffect, useState } from "react";
import FilterButtons from "../components/FilterButtons";
import InputForm from "../components/InputForm";
import TodoList from "../components/TodoList";
import TodoStats from "../components/TodoStats";
import { todoService } from "../services/todoService";

const idsMatch = (left: string | number, right: string | number) =>
  String(left) === String(right);

const Home = () => {
  const [todos, setTodos] = useState<TodoType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [filter, setFilter] = useState<FilterType>("all");

  const getTodos = async () => {
    try {
      setLoading(true);
      const data = await todoService.getAllTodos();
      setTodos(data);
      setError("");
    } catch (error) {
      console.log(error);
      setError("Failed to fetch todos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTodos();
  }, []);

  const addTodo: AddFn = async (text, priority) => {
    try {
      const createdTodo = await todoService.addTodo(text, priority);
      setTodos((currentTodos) => [...currentTodos, createdTodo]);
      setError("");
    } catch (error) {
      console.log(error);
      setError("Failed to add todo. Please try again.");
    }
  };

  const editTodo: EditFn = async (id, newTask) => {
    try {
      const todoToEdit = todos.find((t) => t.id === id);
      if (todoToEdit) {
        const updatedTodo = await todoService.updateTodo(id, {
          ...todoToEdit,
          task: newTask,
        });
        setTodos((currentTodos) =>
          currentTodos.map((todo) =>
            idsMatch(todo.id, id) ? updatedTodo : todo
          )
        );
        setError("");
      }
    } catch (error) {
      console.log(error);
      setError("Failed to edit todo. Please try again.");
    }
  };

  const toggleTodo: ToggleFn = async (item) => {
    try {
      const updatedTodo = await todoService.updateTodo(item.id, {
        ...item,
        isDone: !item.isDone,
      });
      setTodos((currentTodos) =>
        currentTodos.map((todo) =>
          idsMatch(todo.id, item.id) ? updatedTodo : todo
        )
      );
      setError("");
    } catch (error) {
      console.log(error);
      setError("Failed to update todo. Please try again.");
    }
  };

  const deleteTodo: DeleteFn = async (id) => {
    try {
      await todoService.deleteTodo(id);
      setTodos((currentTodos) =>
        currentTodos.filter((todo) => !idsMatch(todo.id, id))
      );
      setError("");
    } catch (error) {
      console.log(error);
      setError("Failed to delete todo. Please try again.");
    }
  };

  const clearCompleted = async () => {
    try {
      const completedTodoIds = todos.filter((t) => t.isDone).map((t) => t.id);
      await todoService.deleteMultipleTodos(completedTodoIds);
      setTodos((currentTodos) => currentTodos.filter((todo) => !todo.isDone));
      setError("");
    } catch (error) {
      console.log(error);
      setError("Failed to clear completed todos.");
    }
  };

  // Filter todos based on current filter
  const filteredTodos = todos.filter((todo) => {
    if (filter === "active") return !todo.isDone;
    if (filter === "completed") return todo.isDone;
    return true; // all
  });

  // Calculate stats
  const stats = {
    total: todos.length,
    active: todos.filter((t) => !t.isDone).length,
    completed: todos.filter((t) => t.isDone).length,
  };

  return (
    <div className="main">
      <InputForm addTodo={addTodo} />

      {todos.length > 0 && (
        <>
          <TodoStats
            total={stats.total}
            active={stats.active}
            completed={stats.completed}
          />
          <FilterButtons
            currentFilter={filter}
            onFilterChange={setFilter}
            onClearCompleted={clearCompleted}
            hasCompletedTodos={stats.completed > 0}
          />
        </>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="todo-list-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading todos...</p>
          </div>
        ) : filteredTodos.length === 0 && todos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <p>No todos yet. Add your first todo!</p>
          </div>
        ) : filteredTodos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>No {filter} todos found!</p>
          </div>
        ) : (
          <TodoList
            todos={filteredTodos}
            toggleTodo={toggleTodo}
            deleteTodo={deleteTodo}
            editTodo={editTodo}
          />
        )}
      </div>
    </div>
  );
};

export default Home;
